import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Observable } from 'rxjs';

import { TsSnackbarService } from 'tsui';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FeatureFlag } from 'app/core/enums';
import { FieldDef } from 'app/core/models';
import { FeatureFlagService } from 'app/core/services/feature-flag.service';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { relatedToTypeFromLabel } from 'app/shared/Utils/common.utils';
import { RelatedToType } from 'app/shared/enums';
import { LockedFieldService } from '../../locked-fields/services/locked-fields.service';

@Component({
  selector: 'app-custom-field-import-dialog',
  templateUrl: './custom-field-import-dialog.component.html',
  styleUrls: ['./custom-field-import-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [LockedFieldService],
})
export class CustomFieldImportDialogComponent extends OnDestroyMixin implements OnInit {
  importFieldsForm: FormGroup;
  types: CustomObject[] = [];
  errors: {
    lineNumber: number;
    message: string;
  }[] = [];
  importedFields: FieldDef[] = [];
  displayedColumns = ['lineNumber', 'message'];
  dataSource: MatTableDataSource<{ lineNumber: number; message: string }>;
  public acceptedFileExtensions: string[] = ['.xlsx, .csv, .xls'];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: { customObjects: CustomObject[]; isCustomObject: boolean; relatedToType: string },
    private readonly matDialogRef: MatDialogRef<CustomFieldImportDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly featureFlagService: FeatureFlagService,
    private readonly lockedFieldService: LockedFieldService,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
  }

  get inputFile(): FormControl {
    return this.importFieldsForm.get('file') as FormControl;
  }

  get hasCustomObjectsEnabled$(): Observable<boolean> {
    return this.featureFlagService.hasFeatureFlag(FeatureFlag.CUSTOM_OBJECTS);
  }

  ngOnInit(): void {
    this.types = this.data.customObjects;
    this.buildForm();
  }

  buildForm(): void {
    const relatedToTypeValue = this.data.isCustomObject
      ? this.data?.relatedToType?.toLowerCase()
      : this.data?.relatedToType;

    this.importFieldsForm = this.formBuilder.group({
      relatedToType: [relatedToTypeFromLabel(relatedToTypeValue), [Validators.required]],
      file: [null, [Validators.required]],
    });
  }

  public onImportFileChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const file: File | null = inputElement.files[0];
    this.importFieldsForm.get('file').setValue(file);
  }

  resetForm() {
    this.errors = [];
    this.dataSource = null;
    this.importFieldsForm.enable();
    this.inputFile.reset();
  }

  save(): void {
    if (this.importFieldsForm.valid && this.importFieldsForm.enabled) {
      this.importFieldsForm.disable();
      this.lockedFieldService
        .importFields(
          this.importFieldsForm.get('file').value as File,
          this.importFieldsForm.get('relatedToType').value as RelatedToType,
        )
        .pipe(untilComponentDestroyed(this))
        .subscribe((res) => {
          const existingFields = this.importedFields.map(({ name }) => name);
          const filteredFields = res.fields.filter((newfield) => {
            return !existingFields.includes(newfield.name);
          });

          this.importedFields = [...this.importedFields, ...filteredFields];

          if (Object.keys(res.errors).length > 0) {
            this.errors = Object.keys(res.errors).map((key) => {
              return {
                lineNumber: Number(key),
                message: res.errors[key],
              };
            });
            this.dataSource = new MatTableDataSource(this.errors);
          } else {
            this.snackBar.open('Custom fields are imported.', 'success');

            this.matDialogRef.close(this.importedFields);
          }
        });
    }
  }

  closeModal(): void {
    this.matDialogRef.close(this.importedFields);
  }
}
